// Public vs hidden test cases.
//
// A test_list row is public only when it carries `hidden: false`. Every other row - including
// every test written before this existed, which has no `hidden` key - is hidden, so existing
// sessions look exactly as they always have.
//   hidden  students see the test's name and whether it passed (the long-standing view)
//   public  students see it as "Example #N", with its input and expected output
// Both kinds are compiled into the harness and graded the same way, and the AI tutor is
// given every test, as before.
//
// Lists show public tests first. That is display order only: test_list keeps the order the
// harness was compiled in, because each student result is matched to its row by position.
// Render through testsInStudentOrder and keep using each row's own `index`.

export const isHiddenTest = (test) => test?.hidden !== false

export const isPublicTest = (test) => test?.hidden === false

export const hasPublicTests = (testList) => Array.isArray(testList) && testList.some(isPublicTest)

// 1-based position of the row at `index` among the rows of its own kind.
const numberWithinKind = (testList, index) => {
  const list = testList || []
  const publicRow = isPublicTest(list[index])
  return list.slice(0, index + 1).filter((test) => isPublicTest(test) === publicRow).length
}

// A Python class exercise's test: a check (testResults.js) instead of an input and an
// expected output.
export const isCheckTest = (test) => typeof test?.check === 'string'

// A check can also be a run of asserts (E2's tests). The harness needs a check to end with an
// expression that is True, so such a check is saved with a last line `True`; students read it,
// and the tutor is told about it, without that line. Keep in step with the backend's
// checkSource (studyTutor/prompts.js).
export const isAssertCheck = (check) => /^\s*assert\b/m.test(String(check ?? ''))
export const checkForStudent = (check) =>
  isAssertCheck(check) ? String(check).replace(/\n\s*True\s*$/, '') : check

// What students call the test: "Example #N" when public, else its own name, read exactly as
// the student views always have (so a blank name stays blank). A public check keeps its own
// name ("Starts empty"), which says what it checks.
export const studentTestName = (testList, index) => {
  const test = (testList || [])[index]
  if (!isPublicTest(test)) return test?.testName
  const example = `Example #${numberWithinKind(testList, index)}`
  return isCheckTest(test) ? test.testName?.trim() || example : example
}

// The instructor editor's title for a case: "Example #N" or "Hidden #N".
export const editorCaseLabel = (testList, index) =>
  `${isPublicTest((testList || [])[index]) ? 'Example' : 'Hidden'} #${numberWithinKind(testList, index)}`

// [{ test, index }] with public rows first, each kind in its saved order. A list without
// public rows comes back in its saved order.
export const testsInStudentOrder = (testList) => {
  const rows = (Array.isArray(testList) ? testList : []).map((test, index) => ({ test, index }))
  return [...rows.filter((row) => isPublicTest(row.test)), ...rows.filter((row) => !isPublicTest(row.test))]
}

// What a student is shown for the test at `index`. `fallbackName` is the list's own
// placeholder for a nameless test (VizmentalIDE has one; TestList shows nothing).
// `hiddenTag` is set only when the list mixes both kinds, so a list of hidden tests alone
// (every existing session) gets no tag and renders as before.
export const studentTestView = (testList, index, fallbackName = '') => {
  const test = (testList || [])[index] || {}
  const name = studentTestName(testList, index) || fallbackName
  if (isHiddenTest(test)) {
    return { hidden: true, name, hiddenTag: hasPublicTests(testList) }
  }
  if (isCheckTest(test)) return { hidden: false, name, hiddenTag: false, check: checkForStudent(test.check) }
  return {
    hidden: false,
    name,
    hiddenTag: false,
    input: test.parameter ?? '',
    expected: test.return ?? '',
  }
}

// One test as the AI tutor is told about it: its check, or its input and expected output.
export const describeTestForTutor = (test) =>
  isCheckTest(test)
    ? isAssertCheck(test.check)
      ? `check (passes when every assert in it holds):\n${checkForStudent(test.check)}`
      : `check (its last line must be True):\n${test.check}`
    : `input: ${test?.parameter ?? ''}, expected output: ${test?.return ?? ''}`
